export const PAYMENT_PLANS = {
  DEPOSIT_ONLY: 'deposit_only',
  PAY_IN_FULL: 'pay_in_full',
} as const

export type PaymentPlan = (typeof PAYMENT_PLANS)[keyof typeof PAYMENT_PLANS]

export interface PaymentPlanDivision {
  single_payment?: boolean | number | null
  installments?: number | null
  deposit?: number | string | null
  team_cost?: number | string | null
  player_single_payment?: boolean | number | null
  player_installments?: number | null
  player_deposit?: number | string | null
  player_cost?: number | string | null
}

export interface PaymentPlanOptions {
  depositAmount: number
  fullAmount: number
}

/**
 * Mirrors the backend's native one-time test exactly
 * (RegistrationService::isNativeOneTimePayment): `(installments ?? 1) === 1 &&
 * single_payment` on the team fields for team reps and the player_* fields for
 * players. When true the division natively collects everything up front: the
 * payment-plan choice does not apply and deposit-flow copy must not render.
 */
export const isNativeOneTimePayment = ({
  isTeamRep,
  division,
}: {
  isTeamRep: boolean
  division?: PaymentPlanDivision | null
}): boolean => {
  if (!division) {
    return false
  }

  const singlePayment = isTeamRep
    ? division.single_payment
    : division.player_single_payment
  const installments =
    (isTeamRep ? division.installments : division.player_installments) ?? 1

  return installments === 1 && !!singlePayment
}

/**
 * Returns the Deposit Only / Pay in Full amounts for the plan selector, or null
 * when the selector must not render: on native one-time divisions (the plan
 * choice does not apply) and when there is no remainder beyond the deposit
 * (cost <= deposit), where both plans would charge the same amount.
 */
export const getPaymentPlanOptions = ({
  isTeamRep,
  division,
}: {
  isTeamRep: boolean
  division?: PaymentPlanDivision | null
}): PaymentPlanOptions | null => {
  if (!division) {
    return null
  }

  const deposit = Number(
    (isTeamRep ? division.deposit : division.player_deposit) ?? 0
  )
  const cost = Number(
    (isTeamRep ? division.team_cost : division.player_cost) ?? 0
  )

  if (isNativeOneTimePayment({ isTeamRep, division })) {
    return null
  }

  if (!Number.isFinite(deposit) || !Number.isFinite(cost) || cost <= deposit) {
    return null
  }

  return {
    depositAmount: deposit,
    fullAmount: cost,
  }
}
